import React, { useState, useEffect } from "react";
import {
  message,
  Alert,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Modal,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { DynamicTable } from "dynamic-table-builder";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import localeData from "dayjs/plugin/localeData";
import Heading from "../../components/Heading";
import API from "../../api";
import urls from "../../api/urls";
import "./style.scss";

// Register plugins once for the entire app. This ensures functions like
// `weekday()` exist on dayjs instances, which Ant Design's DatePicker relies on.
dayjs.extend(weekday);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);

const { DA_ATTENDANCE, DA_ATTENDANCE_EXPORT } = urls;
const { RangePicker, MonthPicker } = DatePicker;

// Export filter types
const EXPORT_FILTER_TYPES = {
  DATE_RANGE: "dateRange",
  MONTH: "month",
  QUARTER: "quarter",
};

// Quarter options
const QUARTER_OPTIONS = [
  { label: "Q1 (Jan-Mar)", value: 1 },
  { label: "Q2 (Apr-Jun)", value: 2 },
  { label: "Q3 (Jul-Sep)", value: 3 },
  { label: "Q4 (Oct-Dec)", value: 4 },
];

const DAAttendance = () => {
  const [authError, setAuthError] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  // Note: total count is kept within tableConfig.pagination; no standalone state needed.
  const [apiError, setApiError] = useState(null);
  const [tableConfig, setTableConfig] = useState(null);

  // Export modal and filter states
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportFilterType, setExportFilterType] = useState(
    EXPORT_FILTER_TYPES.DATE_RANGE
  );
  const [exportDateRange, setExportDateRange] = useState([dayjs(), dayjs()]);
  const [exportMonth, setExportMonth] = useState(dayjs());
  const [exportQuarter, setExportQuarter] = useState(1);
  const [exportYear, setExportYear] = useState(dayjs().year());
  const [isExporting, setIsExporting] = useState(false);

  // Handle authentication error
  const handleAuthError = async () => {
    setAuthError(true);
    message.error(
      "Authentication failed. Please check your token or login again."
    );
  };

  // This gets called by the DynamicTable whenever filters, pagination or sorting change.
  const handleDataFetch = async (params) => {
    try {
      setApiError(null);

      // Sync date ranges: Update export date range when table date range changes
      if (params?.filters?.dateRange && params.filters.dateRange.length === 2) {
        const newDateRange = [
          dayjs(params.filters.dateRange[0]),
          dayjs(params.filters.dateRange[1]),
        ];
        setDateRange(newDateRange);
        // Only sync if export filter type is DATE_RANGE
        if (exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE) {
          setExportDateRange(newDateRange);
        }
      }

      // Format the parameters as required by the API
      const startTime = params?.filters?.dateRange?.[0]
        ? dayjs(params.filters.dateRange[0]).startOf("day").toISOString()
        : dayjs().startOf("day").toISOString();
      const endTime = params?.filters?.dateRange?.[1]
        ? dayjs(params.filters.dateRange[1]).endOf("day").toISOString()
        : dayjs().endOf("day").toISOString();

      // Build the query parameters in the format expected by the API
      const requestParams = {
        startTime,
        endTime,
        pageSize: String(params?.pagination?.pageSize || 10),
        pageNumber: String(params?.pagination?.pageNumber || 1),
      };

      // Add any additional filters
      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value && key !== "dateRange" && key !== "status") {
            requestParams[key] = String(value);
          }
        });
      }

      const response = await API.Api(DA_ATTENDANCE).NodeGetWithPremise(
        requestParams
      );

      if (!response.status) {
        if (response.response?.status === 403) {
          await handleAuthError();
          return { data: [], total: 0 };
        }

        const result = response.response?.data;
        if (response.response?.status === 400) {
          // Handle validation errors
          const errorMessages = Array.isArray(result?.message)
            ? result.message
            : [result?.message || "Validation error occurred"];

          setApiError({
            type: "validation",
            title: result?.error || "Validation Error",
            messages: errorMessages,
            statusCode: result?.statusCode,
          });
          return { data: [], total: 0 };
        }

        throw new Error(result?.message || "Failed to fetch data");
      }

      const result = response.response.data;

      if (result.status === "success") {
        // Update table configuration with API response metadata
        if (result) {
          const newConfig = {
            ...result,
            pagination: {
              ...result.pagination,
              current: parseInt(requestParams.pageNumber, 10),
              pageSize: parseInt(requestParams.pageSize, 10),
              total: result.pagination?.totalCount || 0,
            },
          };
          setTableConfig(newConfig);
        }

        // Ensure data is properly formatted with unique keys
        const dataWithKeys = Array.isArray(result.data)
          ? result.data.map((item) => ({
            ...item,
            key: item.id,
          }))
          : [];

        return {
          data: dataWithKeys,
          total: result.pagination?.totalCount || 0,
          pagination: {
            current: parseInt(requestParams.pageNumber, 10),
            pageSize: parseInt(requestParams.pageSize, 10),
            total: result.pagination?.totalCount || 0,
          },
        };
      } else {
        throw new Error(result.message || "Failed to fetch data");
      }
    } catch (error) {
      if (error.message.includes("403")) {
        await handleAuthError();
      } else {
        const errorMessage = error.message || "An unexpected error occurred";
        setApiError({
          type: "error",
          title: "Error",
          messages: [errorMessage],
          statusCode: error.status || 500,
        });
        message.error(`Error fetching data: ${errorMessage}`);
      }
      return { data: [], total: 0 };
    }
  };

  useEffect(() => {
    handleDataFetch({
      pagination: { pageNumber: 1, pageSize: 10 },
      filters: { dateRange },
    });
  }, []);

  // Handle export functionality with enhanced filters
  const handleExport = async () => {
    try {
      setIsExporting(true);
      setApiError(null);

      // Build export parameters based on selected filter type
      const exportParams = {};

      switch (exportFilterType) {
      case EXPORT_FILTER_TYPES.DATE_RANGE:
        exportParams.startDate =
          exportDateRange[0]?.format("YYYY-MM-DD") || null;
        exportParams.endDate =
          exportDateRange[1]?.format("YYYY-MM-DD") || null;
        exportParams.month = null;
        exportParams.quarter = null;
        break;

      case EXPORT_FILTER_TYPES.MONTH:
        exportParams.month = exportMonth?.month() + 1; // dayjs months are 0-indexed
        exportParams.year = exportMonth?.year() || exportYear;
        exportParams.startDate = null;
        exportParams.endDate = null;
        exportParams.quarter = null;
        break;

      case EXPORT_FILTER_TYPES.QUARTER:
        exportParams.quarter = exportQuarter;
        exportParams.year = exportYear;
        exportParams.month = null;
        exportParams.startDate = null;
        exportParams.endDate = null;
        break;

      default:
        // Fallback to current date range
        exportParams.startDate = dayjs().format("YYYY-MM-DD");
        exportParams.endDate = dayjs().format("YYYY-MM-DD");
        exportParams.month = null;
        exportParams.quarter = null;
      }

      // Create query string
      const queryParams = new URLSearchParams();
      Object.entries(exportParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          queryParams.append(key, String(value));
        }
      });

      const response = await API.Api(
        `${DA_ATTENDANCE_EXPORT}?${queryParams.toString()}`
      ).get();

      if (!response.status) {
        // Handle specific error response format
        const errorData = response.response?.data;

        if (errorData && typeof errorData === "object") {
          // Handle structured error response
          if (
            errorData.status === "error" &&
            errorData.errors &&
            Array.isArray(errorData.errors)
          ) {
            // Multiple validation errors
            const errorMessages = errorData.errors;
            throw new Error(errorMessages.join("; "));
          } else if (errorData.message) {
            // Single error message
            throw new Error(errorData.message);
          }
        }

        // Fallback error message
        throw new Error(response.response?.data?.message || "Export failed");
      }

      // Handle blob response for CSV download
      const blob = new Blob([response.response.data], { type: "text/csv" });

      if (blob.size === 0) {
        throw new Error("Received empty file");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileName = `DA_Attendance_${exportFilterType}_${dayjs().format(
        "YYYY-MM-DD"
      )}.csv`;
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Export completed successfully");
      setIsExportModalVisible(false);
    } catch (error) {
      let errorMessage = "An unexpected error occurred during export";
      let errorType = "error";

      // Handle different types of errors
      if (error?.message) {
        errorMessage = error.message;

        // Check if it's a validation error
        if (
          errorMessage.includes("Date range must be within 30 days") ||
          errorMessage.includes("startDate must be before endDate") ||
          errorMessage.includes("Validation failed")
        ) {
          errorType = "validation";
        }
      }

      // Show user-friendly error message
      message.error(`${errorMessage}`);

      // Set API error for display in the component
      setApiError({
        type: errorType,
        title: errorType === "validation" ? "Validation Error" : "Export Error",
        messages: [errorMessage],
        statusCode: error?.status || (errorType === "validation" ? 400 : 500),
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleTableExport = async (params) => {
    // Sync the current table filters to export filters if needed
    if (params?.filters?.dateRange && params.filters.dateRange.length === 2) {
      const newDateRange = [
        dayjs(params.filters.dateRange[0]),
        dayjs(params.filters.dateRange[1]),
      ];
      if (exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE) {
        setExportDateRange(newDateRange);
      }
    }

    setIsExportModalVisible(true);
    return;
  };

  // Handle closing export modal
  const handleCloseExportModal = () => {
    setIsExportModalVisible(false);
  };

  const renderError = () => {
    if (!apiError && !authError) return null;

    const error = authError
      ? {
        type: "error",
        title: "Authentication Error",
        messages: [
          "Authentication failed. Please check your token or login again.",
        ],
        statusCode: 403,
      }
      : apiError;

    return (
      <Alert
        message={error.title}
        description={
          <div>
            {error.statusCode && (
              <div style={{ marginBottom: "8px" }}>
                Status Code: {error.statusCode}
              </div>
            )}
            {error.messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  color: error.type === "error" ? "#ff4d4f" : "#faad14",
                }}
              >
                • {msg}
              </div>
            ))}
          </div>
        }
        type={error.type === "validation" ? "warning" : "error"}
        showIcon
        style={{ marginBottom: "16px" }}
        closable
        onClose={() => {
          if (authError) {
            setAuthError(false);
          } else {
            setApiError(null);
          }
        }}
      />
    );
  };

  // Render export modal with filters
  const renderExportModal = () => {
    return (
      <Modal
        title="Export Attendance Data"
        open={isExportModalVisible}
        onCancel={handleCloseExportModal}
        className="da-attendance-export-modal"
        footer={[
          <Button key="cancel" onClick={handleCloseExportModal}>
            Cancel
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            loading={isExporting}
            onClick={handleExport}
          >
            Export
          </Button>,
        ]}
        width={520}
      >
        <div className="modal-content">
          <Row gutter={[12, 16]}>
            <Col span={24}>
              <div>
                <label className="export-form-label">
                  Select Export Filter Type:
                </label>
                <Select
                  value={exportFilterType}
                  onChange={setExportFilterType}
                  style={{ width: "100%" }}
                  size="middle"
                  options={[
                    {
                      label: "Date Range",
                      value: EXPORT_FILTER_TYPES.DATE_RANGE,
                    },
                    {
                      label: "Month",
                      value: EXPORT_FILTER_TYPES.MONTH,
                    },
                    {
                      label: "Quarter",
                      value: EXPORT_FILTER_TYPES.QUARTER,
                    },
                  ]}
                />
              </div>
            </Col>

            {exportFilterType === EXPORT_FILTER_TYPES.DATE_RANGE && (
              <Col span={24}>
                <div>
                  <label className="export-form-label">
                    Select Date Range:
                  </label>
                  <RangePicker
                    value={exportDateRange}
                    onChange={setExportDateRange}
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    size="middle"
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    placeholder={["Start Date", "End Date"]}
                  />
                </div>
              </Col>
            )}

            {exportFilterType === EXPORT_FILTER_TYPES.MONTH && (
              <Col span={24}>
                <div>
                  <label className="export-form-label">
                    Select Month & Year:
                  </label>
                  <MonthPicker
                    value={exportMonth}
                    onChange={setExportMonth}
                    format="MMMM YYYY"
                    style={{ width: "100%" }}
                    size="middle"
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("month")
                    }
                    placeholder="Select Month"
                  />
                </div>
              </Col>
            )}

            {exportFilterType === EXPORT_FILTER_TYPES.QUARTER && (
              <>
                <Col span={12}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      Select Quarter:
                    </label>
                    <Select
                      value={exportQuarter}
                      onChange={setExportQuarter}
                      style={{ width: "100%" }}
                      size="middle"
                      options={QUARTER_OPTIONS}
                      placeholder="Select Quarter"
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      Select Year:
                    </label>
                    <DatePicker
                      value={dayjs().year(exportYear)}
                      onChange={(date) =>
                        setExportYear(date?.year() || dayjs().year())
                      }
                      picker="year"
                      format="YYYY"
                      style={{ width: "100%" }}
                      size="middle"
                      disabledDate={(current) =>
                        current && current.year() > dayjs().year()
                      }
                      placeholder="Select Year"
                    />
                  </div>
                </Col>
              </>
            )}
          </Row>
        </div>
      </Modal>
    );
  };

  return (
    <div className="p-1">
      <Heading title="Back" />
      <div className="da-attendance-container">
        {renderError()}
        {tableConfig && (
          <DynamicTable
            config={tableConfig}
            onDataFetch={handleDataFetch}
            onExport={handleTableExport}
            defaultFilters={{
              dateRange: {
                defaultValue: dateRange,
              },
            }}
          />
        )}
        {renderExportModal()}
      </div>
    </div>
  );
};

export default DAAttendance;
